import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, Chip, Divider, IconButton, Link, Tooltip, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import TuneIcon from '@mui/icons-material/Tune';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { getWorkspaceTokens, WORKSPACE_TOP_BAR_MIN_HEIGHT } from './workspaceStyles';
import { ControlButton, getControlTokens } from '../controls';
import AccessTierBadge from '../AccessTierBadge';
import { filterSublabelSx, sectionContainerSx } from '../../../styles';

const DEFAULT_HEADER_GAP_PX = 12;
const DEFAULT_FILTER_GAP_PX = 8;
const FILTER_LAYOUT_STACK_BUFFER_PX = 12;
const FILTER_LAYOUT_INLINE_BUFFER_PX = 24;

const measureElementWidth = (element) => {
  if (!element) return 0;
  const rectWidth = element.getBoundingClientRect?.().width || 0;
  return rectWidth || element.scrollWidth || element.offsetWidth || 0;
};

const getNumericGap = (element, fallback = DEFAULT_FILTER_GAP_PX) => {
  if (!element || typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  const styles = window.getComputedStyle(element);
  const rawGap = styles.columnGap || styles.gap;
  const parsed = Number.parseFloat(rawGap);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const measureChildrenRowWidth = (element) => {
  if (!element) return 0;
  const children = Array.from(element.children);
  if (children.length === 0) return measureElementWidth(element);
  const gap = getNumericGap(element);
  return children.reduce((total, child) => total + measureElementWidth(child), 0)
    + Math.max(0, children.length - 1) * gap;
};

const measureTitleNaturalWidth = (titleGroup, headerWidth) => {
  if (!titleGroup) return 0;
  const rowWidths = Array.from(titleGroup.children).map(measureChildrenRowWidth);
  const naturalWidth = Math.max(...rowWidths, measureElementWidth(titleGroup));
  const inlineTitleCap = Math.min(headerWidth * 0.36, 460);
  return inlineTitleCap > 0 ? Math.min(naturalWidth, inlineTitleCap) : naturalWidth;
};

const measureFilterNaturalWidth = (filterHost) => {
  if (!filterHost) return 0;
  const filterLabel = filterHost.querySelector('[data-testid="workspace-filter-label"]');
  const filterContent = filterHost.querySelector('[data-workspace-filter-content="true"]');
  const labelWidth = measureElementWidth(filterLabel);

  if (!filterContent) return labelWidth || measureElementWidth(filterHost);

  const children = Array.from(filterContent.children);
  const contentGap = getNumericGap(filterContent);
  const controlsWidth = children.length > 0
    ? children.reduce((total, child) => total + measureElementWidth(child), 0)
      + Math.max(0, children.length - 1) * contentGap
    : measureElementWidth(filterContent);

  const hostGap = getNumericGap(filterHost);
  return labelWidth + (labelWidth && controlsWidth ? hostGap : 0) + controlsWidth;
};

function WorkspaceTopBar({
  title,
  icon: TitleIcon,
  chips = [],
  depthLinks = [],
  headerAccessory = null,
  headerActions = null,
  viewSection = null,
  viewSectionLabel = 'Views',
  isMobile = false,
  onOpenNav,
  filtersOpen = true,
  onToggleFilters,
  filterHostRef,
}) {
  const theme = useTheme();
  const tokens = getWorkspaceTokens(theme);
  const controlTokens = getControlTokens(theme, 'objectToolbar');
  const toolbarMinHeight = Math.max(WORKSPACE_TOP_BAR_MIN_HEIGHT, tokens.controlHeight + 24);
  const showDepthLinks = depthLinks.length > 0;
  const hasActions = Boolean(headerAccessory || headerActions || onToggleFilters);
  const rootRef = useRef(null);
  const titleGroupRef = useRef(null);
  const viewSectionRef = useRef(null);
  const filterHostNodeRef = useRef(null);
  const actionsRef = useRef(null);
  const [filterLayout, setFilterLayout] = useState(isMobile ? 'stacked' : 'inline');
  const hasViewSection = Boolean(viewSection);

  const assignFilterHostRef = useCallback((node) => {
    filterHostNodeRef.current = node;
    if (typeof filterHostRef === 'function') {
      filterHostRef(node);
    } else if (filterHostRef) {
      filterHostRef.current = node;
    }
  }, [filterHostRef]);

  const measureLayout = useCallback(() => {
    if (!filtersOpen) {
      setFilterLayout('inline');
      return;
    }

    if (isMobile) {
      setFilterLayout('stacked');
      return;
    }

    const root = rootRef.current;
    const titleGroup = titleGroupRef.current;
    const viewSectionNode = viewSectionRef.current;
    const filterHost = filterHostNodeRef.current;
    if (!root || !titleGroup || !filterHost) return;

    const headerWidth = root.clientWidth || measureElementWidth(root);
    const titleWidth = measureTitleNaturalWidth(titleGroup, headerWidth);
    const viewSectionWidth = measureElementWidth(viewSectionNode);
    const actionsWidth = measureElementWidth(actionsRef.current);
    const filterNaturalWidth = measureFilterNaturalWidth(filterHost);
    if (!headerWidth || !filterNaturalWidth) return;

    const inlineAvailableWidth = headerWidth
      - titleWidth
      - viewSectionWidth
      - actionsWidth
      - (1 + Number(hasViewSection) + Number(hasActions)) * DEFAULT_HEADER_GAP_PX;
    setFilterLayout((currentLayout) => {
      if (currentLayout === 'stacked') {
        return inlineAvailableWidth >= filterNaturalWidth + FILTER_LAYOUT_INLINE_BUFFER_PX
          ? 'inline'
          : 'stacked';
      }

      return inlineAvailableWidth >= filterNaturalWidth + FILTER_LAYOUT_STACK_BUFFER_PX
        ? 'inline'
        : 'stacked';
    });
  }, [filtersOpen, hasActions, hasViewSection, isMobile]);

  useLayoutEffect(() => {
    measureLayout();

    const cleanups = [];
    const nodes = [
      rootRef.current,
      titleGroupRef.current,
      viewSectionRef.current,
      filterHostNodeRef.current,
      actionsRef.current,
    ].filter(Boolean);

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        measureLayout();
      });
      nodes.forEach((node) => resizeObserver.observe(node));
      cleanups.push(() => resizeObserver.disconnect());
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [measureLayout]);

  const hasControlCluster = hasViewSection || filtersOpen;
  const mobileRows = [
    '"title"',
    hasActions ? '"actions"' : null,
    hasControlCluster ? '"controls"' : null,
  ].filter(Boolean);
  const mobileGridTemplateAreas = mobileRows.join(' ');
  const gridTemplateColumns = isMobile
    ? 'minmax(0, 1fr)'
    : hasControlCluster
    ? hasActions
      ? 'minmax(220px, max-content) minmax(0, 1fr) auto'
      : 'minmax(220px, max-content) minmax(0, 1fr)'
    : hasActions
      ? 'minmax(0, 1fr) auto'
      : 'minmax(0, 1fr)';
  const gridTemplateAreas = isMobile
    ? mobileGridTemplateAreas
    : hasControlCluster
      ? `"title controls${hasActions ? ' actions' : ''}"`
      : `"title${hasActions ? ' actions' : ''}"`;
  const titleMaxWidth = isMobile
    ? '100%'
    : hasControlCluster
    ? { xs: '100%', md: 'min(36vw, 460px)', xl: 'max-content' }
    : '100%';

  return (
    <Box
      ref={rootRef}
      data-testid="workspace-top-bar"
      data-workspace-filter-layout={filtersOpen ? filterLayout : 'closed'}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        flex: '0 0 auto',
        display: 'grid',
        gridTemplateColumns,
        gridTemplateAreas,
        alignItems: 'center',
        alignContent: 'center',
        gap: { xs: 0.75, md: 1 },
        px: tokens.outerInset.compact,
        py: 0.5,
        backgroundColor: tokens.toolbar,
        borderBottom: `1px solid ${controlTokens.dividerColor}`,
        minHeight: toolbarMinHeight,
        overflowX: 'clip',
        overflowY: 'visible',
      }}
    >
      <Box
        ref={titleGroupRef}
        data-testid="workspace-top-bar-title-group"
        sx={{
          gridArea: 'title',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.2,
          minWidth: 0,
          maxWidth: titleMaxWidth,
          justifySelf: 'start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, width: '100%' }}>
          {isMobile && (
            <Tooltip title="Open navigation">
              <IconButton size="small" onClick={onOpenNav} aria-label="Open navigation">
                <MenuIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {TitleIcon && (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: `${tokens.radius}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: tokens.muted,
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            >
              <TitleIcon sx={{ fontSize: 17 }} />
            </Box>
          )}
          <Typography
            data-testid="workspace-top-bar-title"
            sx={{
              fontSize: 14,
              fontWeight: 800,
              color: theme.palette.text.primary,
              lineHeight: 1.15,
              whiteSpace: 'normal',
              minWidth: 0,
              wordBreak: 'normal',
              overflowWrap: 'break-word',
            }}
          >
            {title}
          </Typography>
          {chips.map((chip) => {
            if (chip.tier || chip.accessPolicyId || chip.requiredTier) {
              return (
                <AccessTierBadge
                  key={`tier-${chip.accessPolicyId || chip.requiredTier}`}
                  accessPolicyId={chip.accessPolicyId}
                  requiredTier={chip.requiredTier}
                />
              );
            }
            const ChipIcon = chip.icon;
            return (
              <Chip
                key={`${chip.label}-${chip.value || ''}`}
                size="small"
                icon={ChipIcon ? <ChipIcon sx={{ fontSize: '13px !important' }} /> : undefined}
                label={chip.value || chip.label}
                sx={{
                  height: 22,
                  fontSize: 10,
                  color: theme.palette.text.secondary,
                  backgroundColor: tokens.muted,
                  flexShrink: 0,
                }}
              />
            );
          })}
        </Box>

        {showDepthLinks && (
          <Box
            aria-label="Depth navigation"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              pl: isMobile ? 4.5 : TitleIcon ? 4.4 : 0,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {depthLinks.map((link, index) => {
              const LevelIcon = link.icon;
              const content = (
                <>
                  {LevelIcon && <LevelIcon sx={{ fontSize: 12 }} />}
                  {link.label}
                </>
              );

              return (
                <React.Fragment key={link.id}>
                  {index > 0 && (
                    <Typography
                      aria-hidden="true"
                      component="span"
                      sx={{ color: theme.palette.text.disabled, fontSize: 10, lineHeight: 1 }}
                    >
                      /
                    </Typography>
                  )}
                  {link.active || !link.to ? (
                    <Typography
                      component="span"
                      aria-current={link.active ? 'page' : undefined}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.35,
                        color: link.active ? theme.palette.primary.main : theme.palette.text.secondary,
                        fontSize: 10.5,
                        fontWeight: link.active ? 800 : 650,
                        lineHeight: 1.1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {content}
                    </Typography>
                  ) : (
                    <Link
                      component={RouterLink}
                      to={link.to}
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.35,
                        color: theme.palette.text.secondary,
                        fontSize: 10.5,
                        fontWeight: 650,
                        lineHeight: 1.1,
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      {content}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Box>

      {hasControlCluster && (
        <Box
          data-testid="workspace-top-bar-control-cluster"
          sx={{
            gridArea: 'controls',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            justifySelf: 'center',
            columnGap: 0,
            rowGap: 1,
            flexWrap: 'nowrap',
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            minHeight: controlTokens.height,
            overflow: 'hidden',
          }}
        >
          {hasViewSection && (
            <Box
              ref={viewSectionRef}
              data-testid="workspace-top-bar-view-section"
              sx={{
                ...sectionContainerSx,
                justifyContent: 'center',
                gap: 0,
                flex: '0 0 auto',
                minWidth: 0,
                maxWidth: '100%',
                minHeight: controlTokens.height + 14,
                px: { xs: 0.5, md: 1.25 },
                overflow: 'visible',
              }}
            >
              <Box
                data-testid="workspace-top-bar-view-control"
                sx={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', minWidth: 0 }}
              >
                {viewSection}
              </Box>
              <Box
                data-testid="workspace-top-bar-view-label"
                data-filter-sublabel="true"
                sx={{
                  ...filterSublabelSx(theme),
                  pt: 0.25,
                  pointerEvents: 'none',
                }}
              >
                {viewSectionLabel}
              </Box>
            </Box>
          )}

          {hasViewSection && filtersOpen && (
            <Divider
              data-testid="workspace-top-bar-section-divider"
              orientation="vertical"
              flexItem
              sx={{
                display: filterLayout === 'inline' ? 'block' : 'none',
                height: 24,
                my: 'auto',
                borderColor: controlTokens.dividerColor,
              }}
            />
          )}

          <Box
            ref={assignFilterHostRef}
            data-testid="workspace-top-bar-filter-host"
            data-workspace-filter-layout={filtersOpen ? filterLayout : 'closed'}
            data-workspace-hide-filter-label={hasViewSection ? 'true' : undefined}
            sx={{
              display: filtersOpen ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'safe center',
              flex: '0 1 auto',
              minWidth: 0,
              width: '100%',
              maxWidth: '100%',
              minHeight: tokens.controlHeight,
              px: { xs: 0.5, md: 1.25 },
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          />
        </Box>
      )}

      {(headerAccessory || headerActions || onToggleFilters) && (
        <Box
          ref={actionsRef}
          data-testid="workspace-top-bar-actions"
          sx={{
            gridArea: 'actions',
            display: 'flex',
            alignItems: 'center',
            justifySelf: isMobile ? 'start' : 'end',
            alignSelf: 'stretch',
            gap: { xs: 1, md: 2.5 },
            minWidth: 0,
          }}
        >
          {headerAccessory && (
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                alignSelf: 'stretch',
                minWidth: 0,
                flex: '0 1 auto',
                overflow: 'hidden',
              }}
            >
              {headerAccessory}
            </Box>
          )}
          {headerActions && (
            <Box
              data-testid="workspace-top-bar-primary-actions"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                flex: '0 0 auto',
              }}
            >
              {headerActions}
            </Box>
          )}
          {onToggleFilters && (
            <ControlButton
              context="filterBar"
              variant="iconText"
              buttonVariant={filtersOpen ? 'contained' : 'outlined'}
              selected={filtersOpen}
              icon={<TuneIcon fontSize="small" />}
              onClick={onToggleFilters}
              ariaLabel={filtersOpen ? 'Hide filters' : 'Show filters'}
            >
              {filtersOpen ? 'Hide' : 'Filters'}
            </ControlButton>
          )}
        </Box>
      )}
    </Box>
  );
}

export default WorkspaceTopBar;
